import { QToastProps } from '@qualio/ui-components';
import i18n from 'i18next';
import { z } from 'zod';
import {
  ChangeStatusPayload,
  ChangeStatusResponse,
  documentApi,
} from '../../../../../../../api/document';
import { QualioDocument } from '../../../../../../../api/model/document';

const CHANGE_CONTROL_REQUIRED_SLUG = 'change-control-required';

const ChangeStatusErrorResponseDataSchema = z
  .object({
    response: z
      .object({
        data: z
          .object({
            message: z.string().optional(),
            slug: z.string().optional(),
          })
          .passthrough(),
      })
      .passthrough(),
  })
  .transform((result) => result.response.data);

export const ChangeStatusErrorResponseSchema =
  ChangeStatusErrorResponseDataSchema.transform(
    (result) => result.message ?? getDefaultFailureMessage(),
  );

class ChangeStatusError extends Error {
  constructor(
    public document: QualioDocument,
    public message: string,
    public slug?: string,
  ) {
    super(message);
  }
}

const getFailedDocumentLabels = (failedDocumentPromises: ChangeStatusError[]) =>
  failedDocumentPromises
    .map(({ document: { code, title } }) => `${code} ${title}`)
    .join(', ');

export const getDefaultFailureMessage = () =>
  i18n.t('errors.somethingWentWrong', {
    defaultValue: 'Something went wrong. Please try again.',
  });

export const handleStatusChange = async (
  selectedDocs: QualioDocument[],
  changeStatusPayload: ChangeStatusPayload,
  setDocSelection: (selectedDocs: Record<number, boolean>) => void,
  showToast: (options: QToastProps) => void,
  toastActionStringActive: string,
  toastActionStringPassive: string,
) => {
  const processFailedStatusChange = (
    document: QualioDocument,
    error: unknown,
  ) => {
    const parsedError = ChangeStatusErrorResponseDataSchema.safeParse(error);
    if (parsedError.success) {
      const {
        data: { message, slug },
      } = parsedError;
      throw new ChangeStatusError(
        document,
        message ?? getDefaultFailureMessage(),
        slug,
      );
    }
    throw new ChangeStatusError(document, getDefaultFailureMessage());
  };

  const promises: Promise<ChangeStatusResponse>[] = selectedDocs.map(
    (doc: QualioDocument) =>
      documentApi
        .changeStatus(doc.id, changeStatusPayload)
        .catch((error: unknown) => processFailedStatusChange(doc, error)),
  );
  const results = await Promise.allSettled(promises);

  const idsOfFailedDocs: number[] = [];
  const failedDocPromises: ChangeStatusError[] = [];
  results.forEach((result) => {
    if (result.status === 'rejected') {
      idsOfFailedDocs.push(result.reason.document.id);
      failedDocPromises.push(result.reason);
    }
  });
  const numberOfFailedDocs = idsOfFailedDocs.length;
  const numberOfSuccessfullyChangedDocs = results.length - numberOfFailedDocs;
  if (numberOfSuccessfullyChangedDocs > 0) {
    showSuccessToast(
      showToast,
      numberOfSuccessfullyChangedDocs,
      toastActionStringPassive,
      toastActionStringPassive,
    );
  }
  if (numberOfFailedDocs === 1) {
    showSingleFailureToast(
      showToast,
      toastActionStringActive,
      failedDocPromises[0],
    );
  } else if (numberOfFailedDocs > 1) {
    showMultipleFailureToast(
      showToast,
      toastActionStringActive,
      failedDocPromises,
    );
  }
  const updatedSelection = selectedDocs.filter((doc) =>
    idsOfFailedDocs.includes(doc.id),
  );
  const selectionObject: Record<number, boolean> = {};
  updatedSelection.forEach((doc) => {
    selectionObject[doc.id] = true;
  });
  setDocSelection(selectionObject);
};

const showSuccessToast = (
  showToast: (options: QToastProps) => void,
  numberOfSuccessfullyChangedDocs: number,
  toastActionStringPassive: string,
  toastActionStringActive: string,
) => {
  const documentText =
    numberOfSuccessfullyChangedDocs === 1
      ? i18n.t('labels.document', { defaultValue: 'Document' })
      : i18n.t('labels.documents', { defaultValue: 'Documents' });
  const successTitle = `${documentText} ${toastActionStringPassive}!`;
  const successMessage = i18n.t(
    'additionalMessages.documentsSuccessfullyChanged',
    {
      count: numberOfSuccessfullyChangedDocs,
      action: toastActionStringPassive,
      defaultValue:
        '{{count}} {{count, plural, one {document} other {documents}}} successfully {{action}}.',
    },
  );
  showToast({
    'data-cy': 'bulk-v2-change-status-success-toast',
    id: `change-status-${toastActionStringActive}-success-toast`,
    replace: true,
    status: 'success',
    title: successTitle,
    description: successMessage,
  });
};

const showSingleFailureToast = (
  showToast: (options: QToastProps) => void,
  toastActionStringActive: string,
  failedDocumentPromise: ChangeStatusError,
) => {
  const isChangeControlRequired =
    failedDocumentPromise.slug === CHANGE_CONTROL_REQUIRED_SLUG;
  showToast({
    'data-cy': 'bulk-v2-change-status-error-toast',
    id: `change-status-${toastActionStringActive}-error-toast`,
    replace: true,
    status: 'error',
    title: isChangeControlRequired
      ? i18n.t('documentActions.changeControlRequired')
      : i18n.t('notifications.error'),
    description: isChangeControlRequired
      ? i18n.t('documentActions.pleaseCompleteChangeControlsBeforeBulkApproval')
      : failedDocumentPromise.message || getDefaultFailureMessage(),
  });
};

const showMultipleFailureToast = (
  showToast: (options: QToastProps) => void,
  toastActionStringActive: string,
  failedDocumentPromises: ChangeStatusError[],
) => {
  const onlyChangeControlRequiredFailures = failedDocumentPromises.every(
    ({ slug }) => slug === CHANGE_CONTROL_REQUIRED_SLUG,
  );
  const failureCount = failedDocumentPromises.length;
  const failureMessage = onlyChangeControlRequiredFailures
    ? i18n.t('documentActions.pleaseCompleteChangeControlsBeforeBulkApproval')
    : i18n.t('additionalMessages.documentsFailedToChange', {
        count: failureCount,
        action: toastActionStringActive,
        defaultValue: '{{count}} documents failed to {{action}}:',
      });
  const description = `${failureMessage} ${getFailedDocumentLabels(
    failedDocumentPromises,
  )}`;
  showToast({
    'data-cy': 'bulk-v2-change-status-multiple-error-toast',
    id: `change-status-${toastActionStringActive}-multiple-error-toast`,
    replace: true,
    status: 'error',
    title: onlyChangeControlRequiredFailures
      ? i18n.t('documentActions.changeControlRequired')
      : i18n.t('notifications.error'),
    description,
  });
};
